import { FindStoreResult, SearchStoreByAddress, SearchStoreByCoordinates } from 'autogen/swagger/all';
import QuickView, { useQuickView } from 'components/quickView';
import { SearchRequestBody } from 'components/QuickViewSearch/shared/types';
import React, { useState, useContext } from 'react';
import styled from 'styled-components';
import { breakpoint, sizes } from 'variables';
import AbsoluteQuickViewHeader from 'components/quickView/components/QuickViewWithAbsoluteHeader/Header';
import { DefaultQuickViewHeader } from 'components/quickView/components/DefaultQuickViewComponents/Header';
import { OnSearchCallbackContext, OnGeoLocationCallbackContext, FindStoreResultContext } from '../shared/helpers';
import StoreListQuickView from '../storeListQuickview';
import useFindInStoreQuickView from '../shared/hooks';

const StyledQuickView = styled(QuickView.Controlled)`
    ${breakpoint.up(sizes.md)} {
        min-width: initial;
    }
`;

export function FindInStoreProvider({ productId, children }) {
    const [findStoreResult, setFindStoreResult] = useState<FindStoreResult | undefined>(undefined);
    const onlyIfSundays = false;

    const onSearchCallback = React.useCallback(
        (data: SearchRequestBody): void => {
            const { searchQuery: addressQuery } = data;
            SearchStoreByAddress({ addressQuery, productId, onlyIfSundays }).then((response) => {
                setFindStoreResult(response);
            });
        },
        [onlyIfSundays, productId]
    );

    const onGeoLocationCallback = React.useCallback(
        (latitude: number, longitude: number): void => {
            SearchStoreByCoordinates({ latitude, longitude, productId, onlyIfSundays }).then((response) => {
                setFindStoreResult(response);
            });
        },
        [onlyIfSundays, productId]
    );

    return (
        <OnSearchCallbackContext.Provider value={onSearchCallback}>
            <OnGeoLocationCallbackContext.Provider value={onGeoLocationCallback}>
                <FindStoreResultContext.Provider value={findStoreResult}>
                    {children(findStoreResult)}
                </FindStoreResultContext.Provider>
            </OnGeoLocationCallbackContext.Provider>
        </OnSearchCallbackContext.Provider>
    );
}

export function useFindInStoreResult() {
    return useContext(FindStoreResultContext);
}

export default function FindInStoreQuickView() {
    const [{ isOpen, productId, productInfo, ...restProps }, toggle] = useFindInStoreQuickView();

    function closeQuickView() {
        toggle({ isOpen: false });
    }

    return (
        <StyledQuickView
            active={isOpen}
            onClose={closeQuickView}
            position="right"
            headerRenderer={() =>
                productInfo?.image ? (
                    <AbsoluteQuickViewHeader userCanClose onClose={closeQuickView} />
                ) : (
                    <DefaultQuickViewHeader userCanClose onClose={closeQuickView} />
                )
            }
            {...restProps}
        >
            <FindInStoreProvider productId={productId}>
                {(findInStoreResult: FindStoreResult | undefined) =>
                    productInfo && <StoreListQuickView findStoreResult={findInStoreResult} productInfo={productInfo} />
                }
            </FindInStoreProvider>
        </StyledQuickView>
    );
}
